import { useCmsNew } from "@/lib/cms/cms-new-i18n";
/** Aperçus éditeur des blocs en-tête / pied de page — parité markup avec le front public. */

import {
  resolveMenuPreview,
  type NavMenuKey,
  type ResolvedMenuItem,
} from "@/api/cms-puck/api";
import { usePuckEditorLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { usePuck } from "@measured/puck";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Menu, ShoppingBagIcon, UserRound, X } from "lucide-react";
import { useRef, useState } from "react";

const menuLinkCls =
  "font-semibold text-sm 2xl:text-base flex items-center gap-1 text-center";
const footerLinkCls = "text-sm hover:opacity-80 transition-opacity";

type MenuAlign = "left" | "center" | "right";

const alignClass: Record<MenuAlign, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

function MenuDropdownNode({ item }: { item: ResolvedMenuItem }) {
  const linkProps = {
    href: item.url,
    target: item.openInNew ? "_blank" : undefined,
    rel: item.openInNew ? "noopener noreferrer" : undefined,
  };

  if (!item.children?.length) {
    return (
      <DropdownMenuItem asChild>
        <a {...linkProps}>{item.label}</a>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{item.label}</DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem asChild>
          <a {...linkProps}>{item.label}</a>
        </DropdownMenuItem>
        {item.children.map((child, index) => (
          <MenuDropdownNode
            key={`${child.url}-${child.label}-${index}`}
            item={child}
          />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/**
 * Lien du menu MOBILE (aperçu BO), récursif et indenté — parité markup avec le
 * burger du site public (`SiteMenuNav` front).
 */
function PrimaryMobileMenuLink({
  item,
  depth,
}: {
  item: ResolvedMenuItem;
  depth: number;
}) {
  return (
    <>
      <a
        href={item.url}
        target={item.openInNew ? "_blank" : undefined}
        rel={item.openInNew ? "noopener noreferrer" : undefined}
        className={cn(
          "flex items-center gap-2",
          depth === 0
            ? "font-semibold text-sm"
            : "font-medium text-sm text-slate-600",
        )}
        style={depth > 0 ? { paddingLeft: depth * 16 } : undefined}
      >
        {item.label}
      </a>
      {item.children?.map((child, index) => (
        <PrimaryMobileMenuLink
          key={`${child.url}-${child.label}-${index}`}
          item={child}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

/**
 * Boutons d'en-tête regroupés dans le burger (aperçu BO, mock) : compte, CR,
 * boutique, panier, langue — parité avec `HeaderActionsBar layout="mobile"` du
 * site public. Statique (comme les autres aperçus de blocs d'en-tête).
 */
function PrimaryMenuMobileActions({
  buttons,
}: {
  /** Libellés des Boutons de l'en-tête ; `null` = en-tête hérité (maquette fixe). */
  buttons?: string[] | null;
}) {
  const cmsNew = useCmsNew();
  const ctaLabels = buttons ?? [
    cmsNew("cms.new.preview.headerCta.catalogue", "Voir mes œuvres"),
    cmsNew("cms.new.preview.headerCta.shop", "Boutique"),
  ];
  // Parité ancien `MobileTopbar` : compte, boutons de l'en-tête empilés, panier,
  // puis les langues en GROS boutons centrés. Couleurs héritées de l'en-tête.
  return (
    <div className="flex w-full flex-col gap-3 text-current">
      <span className="inline-flex w-fit items-center gap-2 text-sm font-semibold">
        <UserRound size={18} />
        {cmsNew("cms.new.auto.text.profil", "Profil")}
      </span>
      {ctaLabels.map((label, index) => (
        <span
          key={`${label}-${index}`}
          className="inline-flex h-9 w-fit items-center rounded-md bg-slate-900 px-4 text-sm font-semibold text-white"
        >
          {label}
        </span>
      ))}
      <span className="inline-flex w-fit items-center gap-2 text-sm font-semibold">
        <ShoppingBagIcon size={18} />
        {cmsNew("cms.new.preview.headerBasket.label", "Panier")}
      </span>
      <div className="mt-4 flex w-full items-center justify-center gap-14 px-10">
        {["FR", "EN"].map((lng) => (
          <span key={lng} className="text-3xl font-semibold">
            {lng}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Burger MOBILE (aperçu BO) : bouton + panneau déroulant (carte blanche), parité
 * avec le site public. Le panneau regroupe le menu ET tous les boutons d'en-tête.
 * L'ouverture au clic peut être neutralisée dans le canvas Puck (pointer-events
 * des enfants de bloc) ; le but ici est surtout de montrer que l'en-tête passe
 * bien en burger au viewport mobile/tablette.
 */
function PrimaryMenuBurger({
  menu,
  buttons,
}: {
  menu: ResolvedMenuItem[];
  align: MenuAlign;
  buttons?: string[] | null;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [panelTop, setPanelTop] = useState<number | null>(null);

  const toggle = () => {
    setIsOpen((open) => {
      const next = !open;
      if (next) {
        // Haut du panneau = bas de la rangée d'en-tête (panneau pleine largeur).
        const row = rootRef.current?.closest(
          "[data-puck-dropzone]",
        ) as HTMLElement | null;
        const anchor = row ?? rootRef.current;
        setPanelTop(anchor ? anchor.getBoundingClientRect().bottom : null);
      }
      return next;
    });
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={toggle}
        aria-label="Menu"
        aria-expanded={isOpen}
        className="flex items-center justify-center p-2 text-current"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      {isOpen ? (
        // Panneau PLEINE LARGEUR aux couleurs de l'en-tête (parité ancien MobileTopbar).
        <div
          className="fixed inset-x-0 z-[60] flex max-h-[80vh] flex-col gap-5 overflow-y-auto p-5 text-current shadow-md"
          style={{
            top: panelTop ?? undefined,
            backgroundColor: "var(--cms-page-bg, #ffffff)",
            color: "var(--cms-page-fg, #171717)",
          }}
        >
          {menu.map((item, index) => (
            <PrimaryMobileMenuLink
              key={`${item.url}-${item.label}-${index}`}
              item={item}
              depth={0}
            />
          ))}
          <PrimaryMenuMobileActions buttons={buttons} />
        </div>
      ) : null}
    </div>
  );
}

function PrimaryMenuLinks({
  menu,
  align,
  buttons,
}: {
  menu: ResolvedMenuItem[];
  align: MenuAlign;
  buttons?: string[] | null;
}) {
  return (
    <div className={cn("w-full flex", alignClass[align])}>
      <div className="hidden xl:flex items-center gap-5 2xl:gap-10">
        {menu.map((item, index) => {
          if (!item.children?.length) {
            return (
              <a
                key={`${item.url}-${item.label}-${index}`}
                href={item.url}
                target={item.openInNew ? "_blank" : undefined}
                rel={item.openInNew ? "noopener noreferrer" : undefined}
                className={menuLinkCls}
              >
                {item.label}
              </a>
            );
          }
          return (
            <DropdownMenu key={`${item.url}-${item.label}-${index}`}>
              <DropdownMenuTrigger
                className={cn(menuLinkCls, "outline-none")}
              >
                {item.label}
                <ChevronDown size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild>
                  <a
                    href={item.url}
                    target={item.openInNew ? "_blank" : undefined}
                    rel={item.openInNew ? "noopener noreferrer" : undefined}
                  >
                    {item.label}
                  </a>
                </DropdownMenuItem>
                {item.children.map((child, childIndex) => (
                  <MenuDropdownNode
                    key={`${child.url}-${child.label}-${childIndex}`}
                    item={child}
                  />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>
      {/* Burger toujours à DROITE (parité ancien MobileTopbar). */}
      <div className="cms-mobile-burger flex w-full items-center justify-end xl:hidden">
        <PrimaryMenuBurger menu={menu} align={align} buttons={buttons} />
      </div>
    </div>
  );
}

function FooterMenuLinks({
  menu,
  align,
}: {
  menu: ResolvedMenuItem[];
  align: MenuAlign;
}) {
  return (
    <nav
      className={cn(
        "w-full flex flex-wrap items-center gap-x-6 gap-y-2 py-6 text-slate-500",
        alignClass[align],
      )}
    >
      {menu.map((item, index) => {
        if (!item.children?.length) {
          return (
            <a
              key={`${item.url}-${item.label}-${index}`}
              href={item.url}
              target={item.openInNew ? "_blank" : undefined}
              rel={item.openInNew ? "noopener noreferrer" : undefined}
              className={footerLinkCls}
            >
              {item.label}
            </a>
          );
        }
        return (
          <DropdownMenu key={`${item.url}-${item.label}-${index}`}>
            <DropdownMenuTrigger
              className={cn(footerLinkCls, "flex items-center gap-1 outline-none")}
            >
              {item.label}
              <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem asChild>
                <a
                  href={item.url}
                  target={item.openInNew ? "_blank" : undefined}
                  rel={item.openInNew ? "noopener noreferrer" : undefined}
                >
                  {item.label}
                </a>
              </DropdownMenuItem>
              {item.children.map((child, childIndex) => (
                <MenuDropdownNode
                  key={`${child.url}-${child.label}-${childIndex}`}
                  item={child}
                />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </nav>
  );
}

type PuckNodeLike = { type?: string; props?: Record<string, unknown> };

/**
 * Libellés des Boutons de l'en-tête masqués sous le bureau — repris dans le
 * burger, comme sur le site public. `null` = en-tête hérité (bloc `HeaderCta`).
 */
function collectBurgerButtonLabels(nodes: unknown): string[] | null {
  if (!Array.isArray(nodes)) return [];
  const labels: string[] = [];
  for (const node of nodes as PuckNodeLike[]) {
    if (node?.type === "HeaderCta") return null;
    const props = node?.props ?? {};
    if (node?.type === "Button") {
      const v = (props.style as { visibility?: Record<string, boolean> } | undefined)
        ?.visibility;
      if (v?.hideTablet || v?.hideMobile) labels.push(String(props.label ?? ""));
      continue;
    }
    for (const value of Object.values(props)) {
      if (!Array.isArray(value)) continue;
      const inner = collectBurgerButtonLabels(value);
      if (inner === null) return null;
      labels.push(...inner);
    }
  }
  return labels;
}

/** Variante ÉDITEUR : lit l'état Puck pour montrer les vrais boutons dans le burger. */
export function SiteMenuPreviewEditing(props: {
  menuKey: NavMenuKey;
  align?: MenuAlign;
}) {
  const { appState } = usePuck();
  return (
    <SiteMenuPreview
      {...props}
      buttons={collectBurgerButtonLabels(appState.data.content)}
    />
  );
}

export function SiteMenuPreview({
  menuKey,
  align,
  buttons,
}: {
  menuKey: NavMenuKey;
  align?: MenuAlign;
  buttons?: string[] | null;
}) {
  const cmsNew = useCmsNew();
  const locale = usePuckEditorLocale();
  const resolvedAlign = align ?? (menuKey === "footer" ? "center" : "left");
  const { data: menu = [], isLoading } = useQuery({
    queryKey: ["cms-puck", "menu-resolve", menuKey, locale] as const,
    queryFn: () => resolveMenuPreview(menuKey, locale!),
    enabled: !!locale,
  });

  if (!locale) {
    return (
      <p className="text-xs text-slate-400">
        {cmsNew(
          "cms.new.preview.siteMenu.openEditorHint",
          "Ouvrez l'éditeur pour prévisualiser le menu.",
        )}
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-xs text-slate-400">{cmsNew("cms.new.auto.text.chargement_du_menu", "Chargement du menu…")}</p>;
  }

  if (!menu.length) {
    return (
      <p className="text-xs text-slate-400">
        {cmsNew("cms.new.preview.siteMenu.emptyHint", "Menu vide — ajoutez des entrées dans")}{" "}
        <span className="font-medium text-slate-600">{cmsNew("cms.new.auto.text.menus", "Menus")}</span>.
      </p>
    );
  }

  if (menuKey === "footer") {
    return <FooterMenuLinks menu={menu} align={resolvedAlign} />;
  }

  return <PrimaryMenuLinks menu={menu} align={resolvedAlign} buttons={buttons} />;
}

export function SiteLogoPreview() {
  const cmsNew = useCmsNew();
  return (
    <div>
      <span className="block text-xl md:text-[32px] md:leading-8 font-medium pb-1">
        {cmsNew("cms.new.preview.siteLogo.title", "Titre du site")}
      </span>
      <span className="block text-[12px] md:text-base font-bold uppercase text-slate-500">
        {cmsNew("cms.new.preview.siteLogo.subtitle", "Sous-titre du site")}
      </span>
    </div>
  );
}

export function HeaderCtaPreview() {
  const cmsNew = useCmsNew();
  // Desktop-only : en mobile (< xl) ces boutons sont regroupés dans le burger.
  return (
    <div className="cms-header-action hidden items-center gap-3 xl:flex">
      <span className="inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm font-semibold text-white">
        {cmsNew("cms.new.preview.headerCta.catalogue", "Voir mes œuvres")}
      </span>
      <span className="inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm font-semibold text-white">
        {cmsNew("cms.new.preview.headerCta.shop", "Boutique")}
      </span>
    </div>
  );
}

export function HeaderUserAuthPreview() {
  const cmsNew = useCmsNew();
  // Desktop-only : en mobile (< xl) regroupé dans le burger.
  return (
    <span className="cms-header-action hidden h-9 items-center gap-2 px-2 text-sm font-semibold xl:inline-flex">
      <UserRound size={18} />
      <span className="hidden md:inline">{cmsNew("cms.new.auto.text.profil", "Profil")}</span>
    </span>
  );
}

export function HeaderBasketPreview() {
  // Desktop-only : en mobile (< xl) regroupé dans le burger.
  return (
    <span className="cms-header-action hidden h-9 w-9 items-center justify-center xl:inline-flex">
      <ShoppingBagIcon size={18} />
    </span>
  );
}

export function HeaderLanguagePreview() {
  // Desktop-only : en mobile (< xl) regroupé dans le burger.
  return (
    <span className="cms-header-action hidden h-8 items-center rounded-full border px-3 text-xs font-semibold xl:inline-flex">
      FR
    </span>
  );
}
export function FooterContentPreview({ showArchives }: { showArchives?: boolean }) {
  const cmsNew = useCmsNew();
  return (
    <div className="w-full flex flex-col items-center gap-8">
      <div
        className="w-full grid grid-cols-1 md:grid-cols-3 items-center text-base py-10 border-t border-slate-200"
      >
        <div>
          {showArchives !== false ? (
            <>
              <div className="text-lg leading-snug mb-1 text-slate-500">
                {cmsNew("cms.new.preview.footer.siteArchives", "Archives du site")}
              </div>
              <span className="text-sm text-slate-500">
                {cmsNew("cms.new.preview.footer.credit", "Site par InventoZen")}
              </span>
            </>
          ) : null}
        </div>
        <div className="flex gap-4 items-center justify-center py-4 md:py-0">
          {["Instagram", "Facebook"].map((label) => (
            <span
              key={label}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-600"
            >
              {label.slice(0, 2)}
            </span>
          ))}
        </div>
        <div className="flex flex-col items-center md:items-end gap-2">
          <div className="flex flex-wrap gap-4 items-center justify-center md:justify-end text-slate-500">
            {[
              cmsNew("cms.new.preview.footer.legalNotice", "Mentions légales"),
              cmsNew("cms.new.preview.footer.privacy", "Confidentialité"),
              cmsNew("cms.new.preview.footer.terms", "CGU"),
            ].map((label) => (
              <span key={label} className={footerLinkCls}>
                {label}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-600">{cmsNew("cms.new.auto.text.optimise_pour_chrome", "Optimisé pour Chrome")}</p>
        </div>
      </div>
    </div>
  );
}
